/**
 * Home.jsx — marketing landing page.
 * Hero (asymmetric 60/40) → Value-prop bar → Bento services peek →
 * Pull-quote testimonial → CTA panel.
 */
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Calculator, BookOpen, Compass, Globe, Landmark } from "lucide-react";

const heroImg = "https://images.pexels.com/photos/34092/pexels-photo.jpg";

const container = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const item = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.2, 0.8, 0.2, 1] } } };

export default function Home() {
    return (
        <div className="font-outfit" data-testid="marketing-home">
            {/* ─── HERO ─── */}
            <section className="relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 md:pt-24 lg:pt-28 pb-24 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    <motion.div className="lg:col-span-7" variants={container} initial="hidden" animate="show">
                        <motion.div variants={item} className="text-[10px] font-semibold tracking-[0.28em] uppercase text-[#C5A059] mb-5">
                            EST. 2009 · DIBRUGARH · ASSAM · INDIA
                        </motion.div>
                        <motion.h1
                            variants={item}
                            className="font-serif text-5xl md:text-6xl lg:text-[5rem] font-light tracking-tighter leading-[1.02] text-[#0A1128]"
                        >
                            Witnessing <span className="italic text-[#C5A059]">Success,</span><br />
                            Supporting Growth.
                        </motion.h1>
                        <motion.p
                            variants={item}
                            className="mt-7 text-lg lg:text-xl font-light leading-relaxed text-[#0A1128]/70 max-w-xl"
                        >
                            Seventeen years of trusted tax &amp; accounting counsel for government,
                            non-government and NRI clients across India, Japan, Oman and Germany.
                            Now incorporated as <span className="italic">Ghatoshkash Tax Wizards Pvt. Ltd.</span> — the same firm, a stronger foundation.
                        </motion.p>
                        <motion.div variants={item} className="mt-10 flex flex-wrap items-center gap-4">
                            <Link
                                to="/contact"
                                data-testid="hero-book-consultation"
                                className="inline-flex items-center gap-2 bg-[#0A1128] text-[#FAF3E0] px-7 py-3.5 text-[13px] uppercase tracking-[0.18em] font-semibold hover:bg-[#C5A059] hover:text-[#0A1128] transition-colors duration-300"
                            >
                                Book a Consultation <ArrowUpRight size={15} />
                            </Link>
                            <Link
                                to="/services"
                                data-testid="hero-explore-services"
                                className="inline-flex items-center gap-2 border border-[#0A1128] text-[#0A1128] px-7 py-3.5 text-[13px] uppercase tracking-[0.18em] font-semibold hover:bg-[#0A1128]/5 transition-colors duration-300"
                            >
                                Explore Services
                            </Link>
                        </motion.div>
                    </motion.div>

                    <motion.div
                        className="lg:col-span-5 relative"
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1, transition: { duration: 1, delay: 0.2, ease: [0.2, 0.8, 0.2, 1] } }}
                    >
                        <div className="aspect-[3/4] w-full overflow-hidden">
                            <img
                                src={heroImg}
                                alt="Professionals overlooking a city skyline"
                                className="w-full h-full object-cover grayscale-[15%]"
                                loading="eager"
                            />
                        </div>
                        <div className="absolute -bottom-4 -left-4 hidden md:block bg-[#0A1128] text-[#FAF3E0] px-6 py-4">
                            <div className="text-[10px] tracking-[0.25em] uppercase text-[#C5A059]">Trusted since 2009</div>
                            <div className="font-serif italic text-lg mt-1">An ally in every victory.</div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* ─── VALUE PROP BAR ─── */}
            <section className="border-y border-[#0A1128]/10 bg-[#FDFBF7]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-wrap items-center justify-around gap-4 text-[11px] uppercase tracking-[0.22em] font-semibold text-[#0A1128]/70">
                    <span data-testid="vp-years">17 Years of Practice</span>
                    <span className="w-px h-4 bg-[#0A1128]/20 hidden md:block" />
                    <span data-testid="vp-reach">India · Japan · Oman · Germany</span>
                    <span className="w-px h-4 bg-[#0A1128]/20 hidden md:block" />
                    <span data-testid="vp-trust">Government · NRI · Advisory</span>
                    <span className="w-px h-4 bg-[#0A1128]/20 hidden md:block" />
                    <span data-testid="vp-inc">Incorporated 2025</span>
                </div>
            </section>

            {/* ─── STORY PEEK ─── */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                    <div className="text-[10px] font-semibold tracking-[0.28em] uppercase text-[#C5A059] mb-4">
                        A new corporate chapter
                    </div>
                    <h2 className="font-serif text-4xl lg:text-5xl font-normal tracking-tight leading-[1.1] text-[#0A1128]">
                        Same firm,<br />
                        <span className="italic">stronger foundation.</span>
                    </h2>
                </div>
                <div className="lg:col-span-7 lg:pt-4">
                    <p className="text-lg font-light leading-relaxed text-[#0A1128]/75">
                        <b className="font-medium text-[#0A1128]">Tax Solution India</b> was born on
                        25 May 2009 at Banipur Tiniali, Dibrugarh — with a single mission to serve the
                        public and the government in matters of taxation and accounting.
                    </p>
                    <p className="text-lg font-light leading-relaxed text-[#0A1128]/75 mt-5">
                        Nearly seventeen years later, on <b className="font-medium text-[#0A1128]">28 November 2025</b>,
                        the practice matured into a registered private-limited company —{" "}
                        <span className="italic text-[#0A1128]">Ghatoshkash Tax Wizards Pvt. Ltd.</span> — carrying
                        forward the Tax Solution India brand. Registration is legal paperwork; the emotions and dedication behind the milestone are far greater.
                    </p>
                    <Link
                        to="/about"
                        data-testid="story-read-more"
                        className="mt-8 inline-flex items-center gap-1.5 text-[13px] uppercase tracking-[0.18em] font-semibold text-[#0A1128] border-b border-[#C5A059] pb-1 hover:text-[#C5A059] transition-colors duration-300"
                    >
                        Read the full journey <ArrowUpRight size={14} />
                    </Link>
                </div>
            </section>

            {/* ─── SERVICES BENTO ─── */}
            <section className="bg-[#FDFBF7] border-y border-[#0A1128]/10 py-24 md:py-32">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="mb-14 flex flex-wrap items-end justify-between gap-6">
                        <div>
                            <div className="text-[10px] font-semibold tracking-[0.28em] uppercase text-[#C5A059] mb-3">
                                Our Practice
                            </div>
                            <h2 className="font-serif text-4xl lg:text-5xl font-normal tracking-tight leading-tight text-[#0A1128] max-w-xl">
                                Expertise forged<br />across seventeen years.
                            </h2>
                        </div>
                        <Link
                            to="/services"
                            data-testid="services-see-all"
                            className="inline-flex items-center gap-1.5 text-[13px] uppercase tracking-[0.18em] font-semibold text-[#0A1128] hover:text-[#C5A059] transition-colors duration-300"
                        >
                            See all services <ArrowUpRight size={14} />
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 grid-rows-[auto] gap-4">
                        <BentoCard
                            testid="bento-taxation"
                            span="md:col-span-2 md:row-span-2 min-h-[320px]"
                            eyebrow="01 · Core Practice"
                            title="Taxation"
                            body="GST filing · TDS / TCS compliance · Income-tax returns for individuals, firms and companies."
                            Icon={Calculator}
                            dark
                        />
                        <BentoCard testid="bento-accounting" eyebrow="02" title="Accounting" body="Bookkeeping, financial statements and audit support." Icon={BookOpen} />
                        <BentoCard testid="bento-advisory"   eyebrow="03" title="Business Advisory" body="Entity registration, statutory compliance, corporate governance." Icon={Compass} />
                        <BentoCard testid="bento-nri"        eyebrow="04" title="International / NRI" body="Cross-border advisory and global compliance for NRI clients." Icon={Globe} />
                        <BentoCard testid="bento-govt"       eyebrow="05" title="Govt & Non-Govt" body="Institutional accounts &amp; compliance across the north-east." Icon={Landmark} />
                    </div>
                </div>
            </section>

            {/* ─── PULL QUOTE ─── */}
            <section className="bg-[#0A1128] text-[#FAF3E0] py-24 md:py-32">
                <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
                    <div className="text-[#C5A059] font-serif text-6xl leading-none mb-4">&ldquo;</div>
                    <blockquote className="font-serif text-3xl md:text-4xl lg:text-5xl font-light leading-[1.2] italic">
                        Yes, money is the vehicle we all need. But watching our clients
                        prosper is what brings us the greatest joy.
                    </blockquote>
                    <div className="mt-8 text-[11px] uppercase tracking-[0.28em] text-[#C5A059] font-semibold">
                        — Founder&apos;s note · 2026
                    </div>
                </div>
            </section>

            {/* ─── CTA ─── */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-28 text-center">
                <h2 className="font-serif text-4xl lg:text-5xl font-normal tracking-tight text-[#0A1128] leading-tight">
                    Ready to make us your ally?
                </h2>
                <p className="mt-4 text-lg font-light text-[#0A1128]/70 max-w-2xl mx-auto">
                    A short conversation is often enough to see where we can help. Start yours today.
                </p>
                <Link
                    to="/contact"
                    data-testid="cta-book"
                    className="mt-8 inline-flex items-center gap-2 bg-[#0A1128] text-[#FAF3E0] px-8 py-4 text-[13px] uppercase tracking-[0.2em] font-semibold hover:bg-[#C5A059] hover:text-[#0A1128] transition-colors duration-300"
                >
                    Book a Consultation <ArrowUpRight size={16} />
                </Link>
            </section>
        </div>
    );
}

function BentoCard({ testid, span = "", eyebrow, title, body, Icon, dark = false }) {
    return (
        <div
            data-testid={testid}
            className={`${span} group relative flex flex-col justify-between p-8 border transition-all duration-300 ${
                dark
                    ? "bg-[#0A1128] text-[#FAF3E0] border-[#0A1128]"
                    : "bg-[#FAF3E0] text-[#0A1128] border-[#0A1128]/10 hover:border-[#C5A059]"
            } hover:-translate-y-1`}
        >
            <div>
                <div className={`text-[10px] font-semibold tracking-[0.28em] uppercase ${dark ? "text-[#C5A059]" : "text-[#C5A059]"} mb-4`}>
                    {eyebrow}
                </div>
                <h3 className={`font-serif ${dark ? "text-4xl lg:text-5xl" : "text-2xl"} font-medium tracking-tight leading-tight`}>
                    {title}
                </h3>
                <p className={`${dark ? "text-base mt-4 opacity-80 max-w-md" : "text-sm mt-3 opacity-70"} font-light leading-relaxed`}>
                    {body}
                </p>
            </div>
            <div className="mt-6 flex items-end justify-between">
                <Icon size={dark ? 34 : 22} className={dark ? "text-[#C5A059]" : "text-[#C5A059] opacity-80"} strokeWidth={1.2} />
                <ArrowUpRight
                    size={16}
                    className={`opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 ${dark ? "text-[#FAF3E0]" : "text-[#0A1128]"}`}
                />
            </div>
        </div>
    );
}
